import React, { createContext, useContext, useState, useCallback } from 'react';
import { FlashbarProps } from '@amzn/awsui-components-console';
import { v4 } from 'uuid';
import { linkify } from './linkify';

interface ApiErrorContextType {
  errors: FlashbarProps.MessageDefinition[];
  pushError: (
    header: string,
    content: string,
    type?: FlashbarProps.Type
  ) => void;
  dismissError: (id: string) => void;
}

const ApiErrorContext = createContext<ApiErrorContextType>({
  errors: [],
  pushError: () => {},
  dismissError: () => {},
});

export const useApiErrors = () => useContext(ApiErrorContext);

/**
 * Tier 2: React context for API response errors.
 * Any child component can pushError() without prop drilling.
 * Errors persist until page navigation (unmount) or manual dismiss.
 */
export const ApiErrorProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [errors, setErrors] = useState<FlashbarProps.MessageDefinition[]>([]);

  const pushError = useCallback(
    (header: string, content: string, type: FlashbarProps.Type = 'error') => {
      const id = v4();
      setErrors((prev) => [
        ...prev,
        {
          header,
          type,
          content: linkify(content),
          dismissible: true,
          dismissLabel: 'Dismiss message',
          onDismiss: () =>
            setErrors((items) => items.filter((item) => item.id !== id)),
          id,
        },
      ]);
    },
    []
  );

  const dismissError = useCallback((id: string) => {
    setErrors((items) => items.filter((item) => item.id !== id));
  }, []);

  return (
    <ApiErrorContext.Provider value={{ errors, pushError, dismissError }}>
      {children}
    </ApiErrorContext.Provider>
  );
};
